import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Save, X, User, MapPin, Phone, Briefcase, Euro, Globe, FileText } from 'lucide-react';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RetraiteProfileEdit = ({ profile, user, token, onSaved, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    title: profile?.title || '',
    bio: profile?.bio || '',
    skills: (profile?.skills || []).join(', '),
    experience_years: profile?.experience_years || '',
    last_position: profile?.last_position || '',
    last_company: profile?.last_company || '',
    hourly_rate: profile?.hourly_rate || '',
    daily_rate: profile?.daily_rate || '',
    location: profile?.location || '',
    phone: profile?.phone || '',
    availability: profile?.availability || 'flexible',
    work_mode: profile?.work_mode || 'hybrid',
    languages: (profile?.languages || ['Français']).join(', '),
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const res = await fetch(`${API_URL}/api/retraites/me`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Profil mis à jour avec succès');
        onSaved(data.profile);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, icon: Icon, children }) => (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-1.5">
        {Icon && <Icon size={14} className="text-muted-foreground" />}
        {label}
      </label>
      {children}
    </div>
  );

  const inputCls = "w-full h-10 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors";
  const textareaCls = "w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors min-h-[80px] resize-y";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="retraite-profile-edit-form">
      {/* Identity */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <User size={18} /> Identité
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Prénom" icon={User}>
            <input type="text" value={form.first_name} onChange={e => handleChange('first_name', e.target.value)}
              className={inputCls} data-testid="edit-first-name" />
          </Field>
          <Field label="Nom" icon={User}>
            <input type="text" value={form.last_name} onChange={e => handleChange('last_name', e.target.value)}
              className={inputCls} data-testid="edit-last-name" />
          </Field>
          <Field label="Titre professionnel" icon={Briefcase}>
            <input type="text" value={form.title} onChange={e => handleChange('title', e.target.value)}
              placeholder="Ex: Directeur Financier" className={inputCls} data-testid="edit-title" />
          </Field>
          <Field label="Téléphone" icon={Phone}>
            <PhoneInput value={form.phone} onChange={val => handleChange('phone', val || '')}
              data-testid="edit-phone" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Biographie" icon={FileText}>
            <textarea value={form.bio} onChange={e => handleChange('bio', e.target.value)}
              placeholder="Décrivez votre parcours et expertise..." className={textareaCls} data-testid="edit-bio" />
          </Field>
        </div>
      </div>

      {/* Experience */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Briefcase size={18} /> Expérience
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Années d'expérience">
            <input type="number" min="0" max="60" value={form.experience_years}
              onChange={e => handleChange('experience_years', e.target.value)}
              className={inputCls} data-testid="edit-experience-years" />
          </Field>
          <Field label="Dernier poste">
            <input type="text" value={form.last_position} onChange={e => handleChange('last_position', e.target.value)}
              placeholder="Ex: CFO" className={inputCls} data-testid="edit-last-position" />
          </Field>
          <Field label="Dernière entreprise">
            <input type="text" value={form.last_company} onChange={e => handleChange('last_company', e.target.value)}
              placeholder="Ex: Total Energies" className={inputCls} data-testid="edit-last-company" />
          </Field>
          <Field label="Langues (séparées par virgule)">
            <input type="text" value={form.languages} onChange={e => handleChange('languages', e.target.value)}
              placeholder="Français, Anglais, Espagnol" className={inputCls} data-testid="edit-languages" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Compétences (séparées par virgule)">
            <input type="text" value={form.skills} onChange={e => handleChange('skills', e.target.value)}
              placeholder="Finance, Stratégie, Management, Gestion de projet" className={inputCls} data-testid="edit-skills" />
          </Field>
        </div>
      </div>

      {/* Availability & Rates */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Euro size={18} /> Disponibilité et tarifs
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Tarif horaire (EUR)" icon={Euro}>
            <input type="number" min="0" step="0.5" value={form.hourly_rate}
              onChange={e => handleChange('hourly_rate', e.target.value)}
              placeholder="Ex: 120" className={inputCls} data-testid="edit-hourly-rate" />
          </Field>
          <Field label="Tarif journalier (EUR)" icon={Euro}>
            <input type="number" min="0" step="1" value={form.daily_rate}
              onChange={e => handleChange('daily_rate', e.target.value)}
              placeholder="Ex: 800" className={inputCls} data-testid="edit-daily-rate" />
          </Field>
          <Field label="Localisation" icon={MapPin}>
            <input type="text" value={form.location} onChange={e => handleChange('location', e.target.value)}
              placeholder="Ex: Paris, Ile-de-France" className={inputCls} data-testid="edit-location" />
          </Field>
          <Field label="Disponibilité">
            <select value={form.availability} onChange={e => handleChange('availability', e.target.value)}
              className={inputCls} data-testid="edit-availability">
              <option value="immediate">Immédiate</option>
              <option value="flexible">Flexible</option>
              <option value="1_week">Sous 1 semaine</option>
              <option value="2_weeks">Sous 2 semaines</option>
              <option value="1_month">Sous 1 mois</option>
            </select>
          </Field>
          <Field label="Mode de travail">
            <select value={form.work_mode} onChange={e => handleChange('work_mode', e.target.value)}
              className={inputCls} data-testid="edit-work-mode">
              <option value="remote">Télétravail</option>
              <option value="onsite">Sur site</option>
              <option value="hybrid">Hybride</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="edit-cancel-btn">
          <X size={16} className="mr-1" /> Annuler
        </Button>
        <Button type="submit" disabled={saving} data-testid="edit-save-btn">
          {saving ? <><Loader2 size={16} className="mr-1 animate-spin" /> Enregistrement...</> :
            <><Save size={16} className="mr-1" /> Enregistrer</>}
        </Button>
      </div>
    </form>
  );
};

export default RetraiteProfileEdit;
